import type { FitmentFieldDefinition } from '../../app/models/FitmentCatalog';
import type { Vehicle, VehicleCreate } from '../../app/models/Vehicle';
import { getLegacyVehicleFieldValue, normalizeFitmentKey, type FitmentFieldValues } from '../../app/utils/fitmentFields';

export type VehicleEditorErrors = Record<string, string | undefined>;

export const vehicleEditorValues = (fields: FitmentFieldDefinition[], vehicle: Vehicle): FitmentFieldValues =>
    Object.fromEntries(fields.map(field => [normalizeFitmentKey(field), getLegacyVehicleFieldValue(vehicle, field)]));

export const vehicleEditorPayload = (
    fields: FitmentFieldDefinition[], values: FitmentFieldValues, yearTo: string,
): VehicleCreate => {
    // Keep explicit empty values: clearing an optional field differs from omitting
    // a hidden field, which the server preserves from the original record.
    const fieldValues = Object.fromEntries(fields.map(field => {
        const key = normalizeFitmentKey(field);
        return [key, (values[key] || '').trim()];
    }));
    const yearFrom = fieldValues.year ? Number(fieldValues.year) : 0;
    return {
        yearFrom,
        yearTo: yearFrom && yearTo ? Number(yearTo) : yearFrom,
        make: fieldValues.make || '',
        model: fieldValues.model || '',
        submodel: fieldValues.submodel || null,
        fieldValues,
    };
};

export const validateVehicleEditor = (
    fields: FitmentFieldDefinition[], values: FitmentFieldValues, yearTo: string,
): VehicleEditorErrors => {
    const errors: VehicleEditorErrors = {};
    const maxYear = Math.min(new Date().getFullYear() + 5, 2100);
    const validYear = (value: string) => /^\d{4}$/.test(value) && Number(value) >= 1886 && Number(value) <= maxYear;
    fields.forEach(field => {
        const key = normalizeFitmentKey(field);
        const value = (values[key] || '').trim();
        if (field.isRequired && !value) errors[key] = `${field.label} is required`;
        else if (value.length > Math.min(field.maxLength ?? 400, 400)) errors[key] = `${field.label} exceeds its maximum length`;
        else if (value && key === 'year' && !validYear(value)) errors[key] = `Enter a year between 1886 and ${maxYear}`;
        else if (value && field.dataType === 'Number' && !/^[+-]?\d+$/.test(value)) errors[key] = 'Enter a whole number';
        else if (value && field.dataType === 'Decimal' && !/^[+-]?(\d+(\.\d*)?|\.\d+)$/.test(value)) errors[key] = 'Enter a decimal number';
    });
    if (fields.some(field => normalizeFitmentKey(field) === 'year') && yearTo) {
        if (!validYear(yearTo)) errors.yearTo = `Enter a year between 1886 and ${maxYear}`;
        else if (!values.year) errors.yearTo = 'Provide a start year first';
        else if (Number(yearTo) < Number(values.year)) errors.yearTo = 'End year must be greater than or equal to start year';
    }
    return errors;
};
